//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';

interface CachedSearchData {
  hits: any[];
  category: string;
  query: string;
  loopId?: string;
  timestamp: number;
  error?: string;
}

interface SearchContextType {
  searchQuery: string;
  selectedCategory: string;
  searchTriggered: boolean;
  cachedSearchData: CachedSearchData | null;
  originalSearchData: CachedSearchData | null;
  setSearchQuery: (query: string) => void;
  setSelectedCategory: (category: string) => void;
  triggerSearch: () => void;
  resetSearchTrigger: () => void;
  setCachedSearchData: (data: CachedSearchData) => void;
  setOriginalSearchData: (data: CachedSearchData | null) => void;
  restoreOriginalResults: () => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearchContext must be used within SearchProvider');
  }
  return context;
};

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('Issues');
  const [searchTriggered, setSearchTriggered] = useState<boolean>(false);
  const [cachedSearchData, setCachedSearchData] =
    useState<CachedSearchData | null>(null);
  const [originalSearchData, setOriginalSearchData] =
    useState<CachedSearchData | null>(null);

  const triggerSearch = useCallback(() => {
    setSearchTriggered(true);
  }, []);

  const resetSearchTrigger = useCallback(() => {
    setSearchTriggered(false);
  }, []);

  const restoreOriginalResults = () => {
    if (originalSearchData) {
      setCachedSearchData(originalSearchData);
    }
  };

  return (
    <SearchContext.Provider
      value={{
        searchQuery,
        selectedCategory,
        searchTriggered,
        cachedSearchData,
        originalSearchData,
        setSearchQuery,
        setSelectedCategory,
        triggerSearch,
        resetSearchTrigger,
        setCachedSearchData,
        setOriginalSearchData,
        restoreOriginalResults,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
